import React, { useEffect, useState } from "react";
import api, { fmtMoney, formatApiErrorDetail } from "@/lib/api";
import { Mail, Copy, X, Loader2, Send, Sparkles, Trophy, Check } from "lucide-react";
import { toast } from "sonner";

export default function RefundEmailModal({ open, onClose, onRecoveryLogged }) {
  const [draft, setDraft] = useState(null);
  const [bankName, setBankName] = useState("");
  const [model, setModel] = useState("claude-sonnet-4-5");
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [recoveryAmount, setRecoveryAmount] = useState("");
  const [logging, setLogging] = useState(false);
  const [logged, setLogged] = useState(null);

  useEffect(() => {
    if (!open) {
      setDraft(null);
      setBody("");
      setSubject("");
      setRecoveryAmount("");
      setLogged(null);
    }
  }, [open]);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(
        "/statements/draft-refund-email",
        { bank_name: bankName || null, model },
        { timeout: 90000 },
      );
      setDraft(data);
      setSubject(data.subject || "");
      setBody(data.body || "");
      setRecoveryAmount(String(data.total_amount || ""));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logRecovery = async () => {
    const amt = parseFloat(recoveryAmount);
    if (!amt || amt <= 0) {
      toast.error("Enter the refunded amount");
      return;
    }
    setLogging(true);
    try {
      const { data } = await api.post("/statements/fees-recovered", {
        amount: amt,
        note: subject || null,
        bank_name: bankName || null,
      });
      setLogged(data);
      toast.success(`Logged ${fmtMoney(amt)} recovered`);
      onRecoveryLogged?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLogging(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
    toast.success("Email copied");
  };

  const mailtoHref = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm" data-testid="refund-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-6 pb-2 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-forest/8 text-forest flex items-center justify-center">
              <Mail className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-heading text-xl font-medium">Draft fee-refund email</h3>
              <p className="text-xs text-ink-lighter">AI writes a polite request you can send in 30 seconds.</p>
            </div>
          </div>
          <button onClick={onClose} className="text-ink-lighter hover:text-ink p-1" data-testid="refund-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">
          {!draft ? (
            <>
              <label className="text-sm block mb-4">
                <div className="font-medium mb-1.5">Your bank's name <span className="text-ink-lighter font-normal">(optional)</span></div>
                <input
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  placeholder="e.g. Chase, Bank of America, Wells Fargo"
                  data-testid="refund-bank-input"
                  className="w-full bg-white border border-border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest"
                />
              </label>
              <div className="flex items-center justify-between gap-3 mb-5">
                <div className="text-xs text-ink-lighter">Model</div>
                <div className="flex bg-paper rounded-lg p-1 border border-border" data-testid="refund-model-toggle">
                  <button onClick={() => setModel("claude-sonnet-4-5")}
                    className={`px-3 py-1 rounded-md text-xs font-medium ${model === "claude-sonnet-4-5" ? "bg-white text-forest shadow-sm" : "text-ink-light"}`}
                    data-testid="refund-model-claude">
                    Claude Sonnet 4.5
                  </button>
                  <button onClick={() => setModel("gpt-5.2")}
                    className={`px-3 py-1 rounded-md text-xs font-medium ${model === "gpt-5.2" ? "bg-white text-forest shadow-sm" : "text-ink-light"}`}
                    data-testid="refund-model-gpt">
                    GPT-5.2
                  </button>
                </div>
              </div>
              <button onClick={generate} disabled={loading}
                data-testid="refund-generate-button"
                className="btn-forest w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {loading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting…</>
                  : <><Sparkles className="w-4 h-4" /> Draft my email</>}
              </button>
              <div className="text-[11px] text-ink-lighter mt-3 text-center">
                We'll automatically pull your detected fees & NSF charges. Nothing is sent — only drafted.
              </div>
            </>
          ) : (
            <>
              <div className="card-surface bg-forest/5 border-forest/15 p-4 mb-5 text-sm" data-testid="refund-meta">
                <span className="font-medium text-ink">{draft.charge_count} charge{draft.charge_count !== 1 ? "s" : ""}</span>
                <span className="text-ink-light"> · </span>
                <span className="num font-medium text-forest">{fmtMoney(draft.total_amount)}</span>
                <span className="text-ink-light"> total requested · drafted by {draft.model}</span>
              </div>

              <label className="text-sm block mb-3">
                <div className="font-medium mb-1.5">Subject</div>
                <input value={subject} onChange={(e) => setSubject(e.target.value)}
                  data-testid="refund-subject"
                  className="w-full bg-white border border-border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest" />
              </label>
              <label className="text-sm block mb-5">
                <div className="font-medium mb-1.5">Body</div>
                <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={14}
                  data-testid="refund-body"
                  className="w-full bg-white border border-border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest font-mono text-[12.5px] leading-relaxed resize-y" />
              </label>

              <div className="grid grid-cols-2 gap-2">
                <button onClick={copy} className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="refund-copy">
                  <Copy className="w-4 h-4" /> Copy
                </button>
                <a href={mailtoHref} className="btn-forest inline-flex items-center justify-center gap-2" data-testid="refund-mailto">
                  <Send className="w-4 h-4" /> Open in email
                </a>
              </div>

              <div className="mt-5 card-surface bg-forest/5 border-forest/15 p-4" data-testid="log-recovery-card">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-forest text-white flex items-center justify-center shrink-0">
                    <Trophy className="w-4 h-4" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-ink text-sm">Got it refunded? Log it.</div>
                    <div className="text-xs text-ink-light mt-0.5">
                      Every dollar you recover adds to your Fees Saved YTD counter on the Dashboard.
                    </div>
                  </div>
                </div>
                {logged ? (
                  <div className="bg-forest/10 rounded-md px-3 py-2 text-sm text-forest flex items-center gap-2" data-testid="recovery-confirmed">
                    <Check className="w-4 h-4" strokeWidth={2} />
                    <span className="font-medium num">{fmtMoney(logged.amount)}</span>
                    <span className="text-ink-light">logged · keep stacking</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center bg-white border border-border rounded-lg px-3 py-2 flex-1">
                      <span className="text-ink-lighter num text-sm mr-1">$</span>
                      <input
                        type="number" step="0.01" min="0"
                        value={recoveryAmount}
                        onChange={(e) => setRecoveryAmount(e.target.value)}
                        placeholder="0.00"
                        data-testid="recovery-amount-input"
                        className="flex-1 outline-none num text-sm"
                      />
                    </div>
                    <button onClick={logRecovery} disabled={logging || !recoveryAmount}
                      data-testid="log-recovery-button"
                      className="btn-forest text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
                      {logging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trophy className="w-3.5 h-3.5" />}
                      Log refund
                    </button>
                  </div>
                )}
              </div>
              <button onClick={() => setDraft(null)} className="text-xs text-ink-lighter hover:text-ink mt-4 mx-auto block" data-testid="refund-regenerate">
                ← Regenerate with a different bank or model
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
